import { MouseEvent, useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import ListGroup from 'react-bootstrap/ListGroup';
import Spinner from 'react-bootstrap/Spinner';
import { useAtomValue, useSetAtom } from 'jotai';
import { useLocation, useNavigate } from 'react-router';
import { PageId } from '../types';
import { userAtom } from '../atoms/userAtom';
import { selectedMediaAtom, selectedSeasonAtom, selectedEpisodeAtom } from '../atoms/selectedMediaAtoms';
import MediaInfo from '../components/MediaInfo';
import { getProgress, hideProgress } from '../utils/api';
import { LastWatchedExt } from '@shared/types';
import { buildShowIdentifier, collectShowIdentifiers, isYoutubeShow } from '@shared/utils/show';
import { getProgressPercent, isProgressLoading, isSameProgressScope } from '@app/utils/progress';
import { loadingProgressItemAtom, loadingShowIdsAtom, localLoadingProgressItemAtom } from '@app/atoms/uiAtoms';
import './HistoryPage.scss';

const LIMIT = 50;
type HistoryMode = 'all' | 'in-progress' | 'review';

const HistoryPage = () => {
  const user = useAtomValue(userAtom);
  const navigate = useNavigate();
  const location = useLocation();

  const setSelectedMedia = useSetAtom(selectedMediaAtom);
  const setSelectedSeason = useSetAtom(selectedSeasonAtom);
  const setSelectedEpisode = useSetAtom(selectedEpisodeAtom);

  const [items, setItems] = useState<LastWatchedExt[]>([]);
  const loadingShowIds = useAtomValue(loadingShowIdsAtom);
  const loadingProgressItem = useAtomValue(loadingProgressItemAtom);
  const localLoadingProgressItem = useAtomValue(localLoadingProgressItemAtom);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hidingIds, setHidingIds] = useState<Set<string>>(new Set());
  const mode: HistoryMode = useMemo(() => {
    const value = new URLSearchParams(location.search).get('mode');
    if (value === 'in-progress' || value === 'review') {
      return value;
    }
    return 'all';
  }, [location.search]);

  const load = async (nextPage: number) => {
    if (loading) return;

    setLoading(true);
    try {
      const resp = await getProgress(nextPage, LIMIT);
      const list = resp?.list ?? [];

      if (nextPage === 1) {
        setItems(list as LastWatchedExt[]);
      } else {
        setItems(prev => prev.concat(list as LastWatchedExt[]));
      }

      setPage(nextPage);
      setHasMore(list.length === LIMIT);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    load(1);
  }, [user]);

  const getSeasonEpisodeFromItem = (item: LastWatchedExt) => {
    const show: any = item.show;
    if (!show?.seasons) return { season: undefined, episode: undefined };

    const season = item.season != null
      ? show.seasons.find((s: any) => s.num === item.season)
      : undefined;

    const episode = season && item.episode != null
      ? season.episodes.find((e: any) => e.num === item.episode)
      : undefined;

    return { season, episode };
  };

  const handleClick = (item: LastWatchedExt) => {
    const show: any = item.show;
    if (!show) return;

    setSelectedMedia(show);

    let selectedSeason = null;
    let selectedEpisode = null;

    if (item.season != null && show.seasons) {
      selectedSeason = show.seasons.find((s: any) => s.num === item.season) || null;

      if (selectedSeason && item.episode != null) {
        selectedEpisode = selectedSeason.episodes.find((e: any) => e.num === item.episode) || null;
      }
    }

    setSelectedSeason(selectedSeason);
    setSelectedEpisode(selectedEpisode);

    const isYoutube = isYoutubeShow(show);
    const targetPage = isYoutube ? PageId.YOUTUBE : (show.type === 'tvshow' ? PageId.SERIES : PageId.MOVIES);
    const showKey = buildShowIdentifier(show);
    if (!showKey) return;
    navigate(`/${targetPage}/${encodeURIComponent(showKey)}`, { state: { preventAutoOpenCards: true } });
  };

  const handleHide = async (event: MouseEvent<HTMLButtonElement>, item: LastWatchedExt) => {
    event.preventDefault();
    event.stopPropagation();

    const itemId = String(item.id);
    if (hidingIds.has(itemId)) return;

    setHidingIds((prev) => {
      const next = new Set(prev);
      next.add(itemId);
      return next;
    });

    const removedItem = items.find((x) => String(x.id) === itemId);
    setItems((prev) => prev.filter((x) => String(x.id) !== itemId));

    try {
      await hideProgress(itemId, 'user_hidden');
    } catch (err) {
      console.error('Error hiding progress item:', err);
      if (removedItem) {
        setItems((prev) => [removedItem, ...prev]);
      }
    } finally {
      setHidingIds((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  if (!user) return null;

  const filteredItems = items.filter((item) => {
    if (mode === 'all') {
      return true;
    }

    const statuses = item.details?.cardStatuses || {};
    const values = Object.values(statuses);
    const answered = values.length;
    const remembered = values.filter((status) => status === 'remembered').length;
    const loaded = Number(item.details?.loadedCardsTotal) || 0;
    const active = Number(item.details?.activeCardsLimit) || 0;
    const total = Math.max(active || loaded, answered);

    if (mode === 'in-progress') {
      return total > 0 && remembered < total;
    }

    if (mode === 'review') {
      return values.some((status) => status === 'not_remembered');
    }

    return true;
  });

  const canLoadMore = hasMore;
  const isItemLoading = (item: LastWatchedExt) => {
    if (isProgressLoading(item)) {
      return true;
    }
    if (loadingProgressItem || localLoadingProgressItem) {
      return isSameProgressScope(item, loadingProgressItem || localLoadingProgressItem);
    }
    const ids = collectShowIdentifiers(item.show as any, [item.screenmediaId]);
    return ids.some((id) => loadingShowIds.has(id));
  };
  const modeTitle = mode === 'in-progress'
    ? 'In progress'
    : mode === 'review'
      ? 'Needs review'
      : 'Viewed Materials';
  const hasModeFilter = mode !== 'all';

  return (
      <div className="d-flex flex-column h-100">
        <div className="history-block row row-cols-1">
          <div className="col">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h2 className="m-0 flex-grow-1 text-center">{modeTitle}</h2>
              {hasModeFilter && (
                <Button
                  variant="link"
                  className="p-0 me-2"
                  onClick={() => navigate('/history')}
                >
                  Clear filter
                </Button>
              )}
              <Button variant="link" className="p-0" onClick={() => navigate('/')}>
                Back
              </Button>
            </div>
          </div>
        </div>
        <div className="history-block row row-cols-1">
          <div className="col history-list">
            <ListGroup className="w-100">
              {filteredItems.map((it) => {
                const { season, episode } = getSeasonEpisodeFromItem(it);

                return (
                  <ListGroup.Item key={String(it.id)} className="p-0 border-0 mb-2">
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-secondary"
                        className="d-flex flex-grow-1"
                        onClick={() => handleClick(it)}
                      >
                        <MediaInfo
                          media={it.show as any}
                          season={season}
                          episode={episode}
                          progressPercent={getProgressPercent(it)}
                          isLoading={isItemLoading(it)}
                        />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        className="px-2"
                        title="Hide from history"
                        disabled={hidingIds.has(String(it.id))}
                        onClick={(event) => handleHide(event, it)}
                      >
                        x
                      </Button>
                    </div>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
            {!loading && filteredItems.length === 0 && (
              <div className="text-center text-muted py-3">
                No items for this filter yet.
              </div>
            )}

            {canLoadMore && (
              <div className="d-flex justify-content-center mt-2">
                <Button
                  variant="outline-secondary"
                  disabled={loading}
                  onClick={() => load(page + 1)}
                >
                  {loading ? <Spinner size="sm"/> : 'Load more'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
  );
};

export default HistoryPage;
